import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { findPanScenario, PAN_SCENARIOS } from "@/data/pan-card-scenarios";

export const Route = createFileRoute("/en/pan-card/$scenario")({
  loader: ({ params }) => {
    const s = findPanScenario(params.scenario);
    if (!s) throw notFound();
    return { s };
  },
  head: ({ params }) => {
    const s = findPanScenario(params.scenario);
    if (!s) return { meta: [{ title: ctaTitle("Not found", "/en/pan-card/$scenario", "en")}, { name: "robots", content: "noindex" }] };
    const path = `/en/pan-card/${s.slug}`;
    const TITLE = `India PAN Card for ${s.en} (Form ${s.form}) — from Thailand | NYC`;
    const DESC = `Indian PAN Card service for ${s.en} using Form ${s.form}, ${s.turnaround}. ${s.reason} — Send your documents for a free check.`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/pan-card/$scenario", "en")}, { name: "description", content: ctaDesc(DESC, "/en/pan-card/$scenario", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/pan-card/$scenario", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/pan-card/$scenario", "en")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/pan-card/${s.slug}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `PAN Card — ${s.en}`, priceFromThb: s.priceFrom,
        steps: [
          { name: "Eligibility", text: `Confirm ${s.en} scope — use Form ${s.form}` },
          { name: "Documents", text: s.docs.join(" · ") },
          { name: "Translate + Notary + Apostille", text: "Foreign documents chained through Apostille (Hague) or Embassy." },
          { name: "File with NSDL/UTIITSL", text: "Online filing + courier to Mumbai." },
          { name: "e-PAN + physical card", text: `Delivered in ${s.turnaround}` },
        ],
        faqs: [
          { q: `Why does ${s.en} need a PAN?`, a: s.reason },
          { q: "Which form?", a: `Form ${s.form}` },
          { q: "Required documents?", a: s.docs.join(" · ") },
          { q: "Pricing and timeline?", a: `From ฿${s.priceFrom.toLocaleString()}, ${s.turnaround}.` },
          { q: "e-PAN vs physical?", a: "e-PAN is a digitally-signed PDF issued directly by the Income Tax Department and is fully valid for every legal, banking, tax filing, mutual-fund KYC, and property-registration purpose in India. The physical laminated card is only a courier artefact — it ships from NSDL Pune to Thailand via India Post/DHL 10–15 business days after e-PAN issuance and is not required for any online workflow." },
        ],
        breadcrumbs: [
          { name: "Home", path: "/en" },
          { name: "PAN Card", path: "/en/pan-card" },
          { name: s.en, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">Scenario not found</h1></main>
  ),
});

function Page() {
  const { s } = Route.useLoaderData();
  return (
    <ServiceLanding
      badge={`PAN · Form ${s.form}`}
      title={`India PAN Card for ${s.en}`}
      subtitle={s.reason}
      quickAnswer={{
        q: `Does ${s.en} need an Indian PAN Card?`,
        a: `${s.reason} — Form ${s.form}, from ฿${s.priceFrom.toLocaleString()}, ${s.turnaround}. Filed entirely from Thailand.`,
      }}
      heroBullets={[
        `Form ${s.form} — ${s.who}`,
        `From ฿${s.priceFrom.toLocaleString()}`,
        `${s.turnaround}`,
        "Translate + Notary + Apostille bundled",
      ]}
      whoFor={{ th: [s.who], foreign: [s.en] }}
      steps={[
        { name: "Eligibility", text: s.who },
        { name: "Documents", text: s.docs.join(" · ") },
        { name: "Translate+Notary+Apostille", text: "Foreign docs chained for Indian acceptance" },
        { name: "File with NSDL", text: "Online + courier" },
        { name: "Receive PAN", text: `e-PAN + physical ${s.turnaround}` },
      ]}
      pricing={[
        { label: `Full-service PAN — ${s.en}`, price: `${s.priceFrom.toLocaleString()}+`, note: s.turnaround },
        { label: "Translate + Notary + Apostille", price: "Included", note: "per document count" },
        { label: "Express NSDL priority", price: "+2,000", note: "cuts 5–7 days" },
      ]}
      faqs={[
        { q: `Why does ${s.en} need PAN?`, a: s.reason },
        { q: "Required documents?", a: s.docs.join(" · ") },
        { q: "Which form?", a: `Form ${s.form}` },
        { q: "Pricing and timeline?", a: `From ฿${s.priceFrom.toLocaleString()}, ${s.turnaround}.` },
        { q: "Travel needed?", a: "No — the entire PAN application is filed online with NSDL (Protean eGov) using scanned passport, photo, and signature; Aadhaar-based biometric e-KYC is skipped for non-residents so no visit to India is ever required. NYC handles the Form 49AA preparation, digital signature attachment, courier of the physical card to Bangkok, and issues the e-PAN PDF within 5–10 business days of submission." },
      ]}
      relatedLinks={[
        { to: "/en/pan-card", label: "← PAN Card hub" },
        ...PAN_SCENARIOS.filter((x) => x.slug !== s.slug).slice(0, 4).map((x) => ({
          to: `/en/pan-card/${x.slug}` as const, label: `PAN — ${x.en}`,
        })) as any,
        { to: "/en/tax", label: "India–Thailand DTAA" },
      ]}
    />
  );
}
